import { Component, Input, OnDestroy, OnInit } from "@angular/core"
import { ModalRef } from "../modal/modal-ref"
import { EduComponent } from "../../utils/eduComponent"
import { FoldersService } from "../../services/folders.service"
import { FolderItem, SelectableFolder } from "../../models/folders.model"
import { Observable, Subject, zip } from "rxjs"
import { NotificationsService } from "angular2-notifications"
import Utils from "../../utils/utils"
import { takeUntil, tap } from "rxjs/operators"

@Component({
    selector: "app-add-to-folder",
    templateUrl: "./add-to-folder.component.html",
    styleUrls: ["./add-to-folder.component.scss"],
})
export class AddToFolderComponent extends EduComponent implements OnInit, OnDestroy {
    @Input() item: FolderItem
    @Input() assignmentId: number
    public folders: SelectableFolder[]
    public isNewFolderSelected: boolean = false
    public newFolderName: string = ""
    private rootFolderId
    private destroyed$: Subject<void> = new Subject<void>()

    constructor(
        public activeModal: ModalRef,
        public foldersService: FoldersService,
        private notificationService: NotificationsService
    ) {
        super()
    }

    public ngOnInit() {
        this.addSub(
            this.foldersService
                .getFoldersForItem(this.item, this.assignmentId)
                .subscribe((data) => {
                    this.folders = data.folders
                    this.rootFolderId = data.rootFolderId
                })
        )
    }

    public saveUpdates() {
        if (this.isNewFolderSelected) {
            this.addSub(
                this.foldersService
                    .createNewFolder(this.newFolderName, this.rootFolderId)
                    .subscribe((data) => {
                        if (!data.folder || data.status === "form_error") {
                            this.notificationService.error(
                                "Could not create new folder",
                                "Please make sure the folder name is new.",
                                Utils.alertConfiguration
                            )
                        } else {
                            const extra = this.foldersService.addToFolder(
                                data.folder.pk,
                                this.item.folderItemTypeKeyName,
                                this.assignmentId
                            )
                            this.updateExistingFolders(extra)
                        }
                    })
            )
        } else {
            this.updateExistingFolders()
        }
    }

    private updateExistingFolders(extraAddNewFolder = null) {
        const calls: Observable<unknown>[] = this.folders
            .filter((f: SelectableFolder): boolean => f.isSelected !== f.wasInitiallySelected)
            .map(
                (wrapper: SelectableFolder): Observable<unknown> => {
                    if (wrapper.isSelected) {
                        return this.foldersService
                            .addToFolder(
                                wrapper.folder.pk,
                                this.item.folderItemTypeKeyName,
                                this.assignmentId
                            )
                            .pipe(
                                takeUntil(this.destroyed$),
                                tap(() =>
                                    this.notificationService.success(
                                        "Success",
                                        "Item added to folder.",
                                        Utils.alertConfiguration
                                    )
                                )
                            )
                    } else {
                        return this.foldersService
                            .removeFromFolder(
                                wrapper.folder.pk,
                                this.item.folderItemTypeKeyName,
                                this.assignmentId
                            )
                            .pipe(
                                takeUntil(this.destroyed$),
                                tap(() =>
                                    this.notificationService.success(
                                        "Success",
                                        "Item removed from folder.",
                                        Utils.alertConfiguration
                                    )
                                )
                            )
                    }
                }
            )
        if (extraAddNewFolder) {
            calls.push(extraAddNewFolder)
        }
        // We're not showing the user an extra pop-up "Item added to folder"
        // tslint:disable-next-line:no-empty
        zip(...calls).subscribe()
    }

    ngOnDestroy() {
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
